import { useState, useEffect } from 'react'
import { supabase } from './supabase'

export default function TableLobby({ session, tableId, onBack, onOpenTable }) {
  const [table, setTable] = useState(null)
  const [players, setPlayers] = useState([])
  const [profiles, setProfiles] = useState({})
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [showInvite, setShowInvite] = useState(false)
  const [pendingLoss, setPendingLoss] = useState(null)
  const [acting, setActing] = useState(false)

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => {
    loadAll()
    const interval = setInterval(loadAll, 5000)
    return () => clearInterval(interval)
  }, [tableId])

  const loadAll = async () => {
    const { data: t } = await supabase.from('crossbet_tables').select('*').eq('id', tableId).single()
    if (!t) return
    setTable(t)
    const { data: tp } = await supabase.from('crossbet_table_players').select('*').eq('table_id', tableId)
    setPlayers(tp || [])
    const ids = [...new Set((tp || []).map(p => p.player_id).concat(t.created_by, t.scorekeeper_id).filter(Boolean))]
    if (ids.length > 0) {
      const { data: prof } = await supabase.from('profiles').select('id, first_name, last_name, username').in('id', ids)
      const pmap = {}
      prof?.forEach(p => { pmap[p.id] = p })
      setProfiles(pmap)
    }
    // Auto-declare winner when 1 active player left
    const active = (tp || []).filter(p => p.status === 'accepted')
    if (t.status === 'active' && active.length === 1) {
      await supabase.from('crossbet_tables').update({ status: 'settled', winner_id: active[0].player_id }).eq('id', t.id)
    }
    setLoading(false)
  }

  const [friendIds, setFriendIds] = useState([])

  useEffect(() => {
    const loadFriends = async () => {
      const { data: f } = await supabase.from('friends').select('user_id, friend_id')
        .or(`user_id.eq.${session.user.id},friend_id.eq.${session.user.id}`)
        .eq('status', 'accepted')
      const ids = (f || []).map(r => r.user_id === session.user.id ? r.friend_id : r.user_id)
      setFriendIds(ids)
    }
    loadFriends()
  }, [session])

  const handleSearch = async (q) => {
    setSearchQuery(q)
    if (q.length < 2) { setSearchResults([]); return }
    const { data } = await supabase.from('profiles').select('id, first_name, last_name, username')
      .neq('id', session.user.id)
      .or(`username.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%`)
      .limit(20)
    const results = data || []
    const sorted = [
      ...results.filter(p => friendIds.includes(p.id)),
      ...results.filter(p => !friendIds.includes(p.id)),
    ]
    setSearchResults(sorted.slice(0, 6))
  }

  const handleInvite = async (player) => {
    if (players.find(p => p.player_id === player.id)) return showToast('Already invited', 'error')
    const { error } = await supabase.from('crossbet_table_players').insert({
      table_id: tableId, player_id: player.id, status: 'invited',
      lives: table?.lives_per_player || 1, total_paid: 0,
    })
    if (error) return showToast(error.message, 'error')
    showToast(`${player.first_name} invited!`, 'success')
    setSearchQuery(''); setSearchResults([]); loadAll()
  }

  const handleLoseLife = async (playerId) => {
    if (pendingLoss !== playerId) {
      setPendingLoss(playerId)
      setTimeout(() => setPendingLoss(null), 3000)
      return
    }
    setPendingLoss(null)
    const tp = players.find(p => p.player_id === playerId)
    if (!tp) return
    const newLives = (tp.lives || 1) - 1
    if (newLives <= 0) {
      await supabase.from('crossbet_table_players').update({ lives: 0, status: 'eliminated', eliminated_at: new Date().toISOString() }).eq('id', tp.id)
      showToast(`${profiles[playerId]?.first_name} eliminated!`, 'info')
    } else {
      await supabase.from('crossbet_table_players').update({ lives: newLives }).eq('id', tp.id)
      showToast(`${profiles[playerId]?.first_name} loses a life — ${newLives} left`, 'info')
    }
    loadAll()
  }

  const handleRebuy = async (playerId) => {
    const tp = players.find(p => p.player_id === playerId)
    if (!tp) return
    const cost = table.rebuy_cost || table.wager_amount
    await supabase.from('crossbet_table_players').update({
      lives: table.rebuy_lives || 1, status: 'accepted',
      rebuys: (tp.rebuys || 0) + 1, total_paid: (tp.total_paid || 0) + cost, eliminated_at: null,
    }).eq('id', tp.id)
    showToast('Rebuy! Back in', 'success'); loadAll()
  }

  const handleStartTable = async () => {
    await supabase.from('crossbet_tables').update({ status: 'active' }).eq('id', tableId)
    showToast('Table is active!', 'success'); loadAll()
  }

  const handleNextRound = async () => {
    if (acting) return
    setActing(true)
    // Get current round number from table name or default
    const currentRound = table.round_number || 1
    const newRound = currentRound + 1
    const newName = table.name.replace(/ — Round \d+$/, '') + ` — Round ${newRound}`

    // Create new table with same settings
    const { data: newTable, error } = await supabase.from('crossbet_tables').insert({
      created_by: table.created_by,
      name: newName,
      wager_amount: table.wager_amount,
      payout_format: table.payout_format,
      lives_enabled: table.lives_enabled,
      lives_per_player: table.lives_per_player,
      rebuy_allowed: table.rebuy_allowed,
      rebuy_cost: table.rebuy_cost,
      rebuy_lives: table.rebuy_lives,
      scorekeeper_id: table.scorekeeper_id,
      split_config: table.split_config,
      scheduled_at: null,
      status: 'pending',
      round_number: newRound,
    }).select().single()

    if (error || !newTable) { showToast('Error creating next round', 'error'); setActing(false); return }

    // Auto-invite all accepted players from current table
    const accepted = players.filter(p => p.status === 'accepted' || p.status === 'eliminated')
    for (const p of accepted) {
      const isCreator = p.player_id === table.created_by
      await supabase.from('crossbet_table_players').insert({
        table_id: newTable.id,
        player_id: p.player_id,
        status: isCreator ? 'accepted' : 'invited',
        lives: newTable.lives_per_player || 1,
        total_paid: isCreator ? newTable.wager_amount : 0,
        joined_at: isCreator ? new Date().toISOString() : null,
      })
    }

    // Close current table
    const { data: ct } = await supabase.from('crossbet_tables').select('winner_id').eq('id', tableId).single()
    await supabase.from('crossbet_tables').update({ status: ct?.winner_id ? 'settled' : 'cancelled' }).eq('id', tableId)
    showToast(`Round ${newRound} created! Check Active Crossbets`, 'success')
    setActing(false)
    setTimeout(() => onBack(), 1500)
  }

  const handleCloseTable = async () => {
    const finalStatus = table?.winner_id ? 'settled' : 'cancelled'
    await supabase.from('crossbet_tables').update({ status: finalStatus }).eq('id', tableId)
    showToast(finalStatus === 'settled' ? 'Table settled' : 'Table closed — funds returned', 'info')
    setTimeout(() => onBack(), 1000)
  }

  const isCreator = table?.created_by === session.user.id
  const canScore = isCreator || table?.scorekeeper_id === session.user.id
  const activePlayers = players.filter(p => p.status === 'accepted')
  const eliminatedPlayers = players.filter(p => p.status === 'eliminated')
  const pot = players.reduce((a, p) => a + (p.total_paid || 0), 0)
  const getName = (id) => profiles[id] ? `${profiles[id].first_name} ${profiles[id].last_name || ''}`.trim() : '?'

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#7A7570', fontFamily: 'DM Sans, sans-serif' }}>Loading...</div>
    </div>
  )

  return (
    <div style={s.screen}>
      {toast && <div style={{ ...s.toast, background: toast.type === 'error' ? '#C0392B' : toast.type === 'success' ? '#00563F' : '#1A1814' }}>{toast.msg}</div>}
      <div style={s.body}>

        {/* Pot */}
        <div style={s.potCard}>
          <div>
            <div style={s.potLabel}>POT</div>
            <div style={s.potAmt}>${pot.toFixed(0)}</div>
            <div style={s.potSub}>{table?.payout_format === 'winner_takes_all' ? 'Winner Takes All' : 'Custom Split'}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            {table?.round_number > 1 && <div style={s.roundBadge}>ROUND {table.round_number}</div>}
            <div style={{ ...s.statusBadge, color: table?.status === 'active' ? '#00563F' : table?.status === 'settled' ? '#7A7570' : '#C19A6B' }}>
              {table?.status?.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Winner */}
        {table?.status === 'settled' && table?.winner_id && (
          <div style={s.winnerBanner}>🏆 {getName(table.winner_id)} wins ${pot.toFixed(0)}!</div>
        )}

        {/* Start */}
        {table?.status === 'pending' && isCreator && activePlayers.length >= 2 && (
          <button style={s.startBtn} onClick={handleStartTable}>
            Start Table — {activePlayers.length} players ready
          </button>
        )}

        {/* Pending notice for non-creator */}
        {table?.status === 'pending' && !isCreator && (
          <div style={s.pendingNotice}>⏳ Waiting for the scorekeeper to start the table</div>
        )}

        {/* Players */}
        <div style={s.sectionTitle}>PLAYERS ({activePlayers.length})</div>
        <div style={s.playersList}>
          {activePlayers.length === 0 ? (
            <div style={s.emptyRow}>No active players yet</div>
          ) : activePlayers.map(tp => {
            const isMe = tp.player_id === session.user.id
            return (
              <div key={tp.id} style={s.playerRow}>
                <div style={s.playerInfo}>
                  <div style={s.playerName}>
                    {getName(tp.player_id)}
                    {isMe && <span style={s.youTag}> (you)</span>}
                    {tp.player_id === table?.scorekeeper_id && <span style={s.skTag}> 🎯</span>}
                  </div>
                  {tp.rebuys > 0 && <div style={s.rebuyTag}>↩ {tp.rebuys} rebuy{tp.rebuys > 1 ? 's' : ''}</div>}
                </div>
                <div style={s.playerRight}>
                  {table?.lives_enabled && (
                    <div style={s.livesRow}>
                      {Array.from({ length: table.lives_per_player }).map((_, i) => (
                        <div key={i} style={{ ...s.lifeHeart, background: i < tp.lives ? '#C0392B' : '#E0DDD4' }}>♥</div>
                      ))}
                    </div>
                  )}
                  {table?.status === 'active' && (canScore || isMe) && (
                    <button style={{ ...s.loseLifeBtn, ...(pendingLoss === tp.player_id ? s.loseLifeBtnConfirm : {}) }}
                      onClick={() => handleLoseLife(tp.player_id)}>
                      {pendingLoss === tp.player_id ? 'Confirm?' : table?.lives_enabled ? '−♥' : 'Out'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Eliminated */}
        {eliminatedPlayers.length > 0 && (
          <>
            <div style={s.sectionTitle}>ELIMINATED</div>
            <div style={s.playersList}>
              {eliminatedPlayers.map(tp => (
                <div key={tp.id} style={{ ...s.playerRow, opacity: 0.5 }}>
                  <div style={s.playerInfo}><div style={s.playerName}>{getName(tp.player_id)}</div></div>
                  {table?.rebuy_allowed && canScore && (
                    <button style={s.rebuyBtn} onClick={() => handleRebuy(tp.player_id)}>
                      ↩ Rebuy ${table.rebuy_cost || table.wager_amount}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Invited */}
        {players.filter(p => p.status === 'invited').length > 0 && (
          <>
            <div style={s.sectionTitle}>INVITED</div>
            <div style={s.playersList}>
              {players.filter(p => p.status === 'invited').map(tp => (
                <div key={tp.id} style={{ ...s.playerRow, opacity: 0.6 }}>
                  <div style={s.playerInfo}><div style={s.playerName}>{getName(tp.player_id)}</div></div>
                  <div style={{ fontSize: 11, color: '#C19A6B', fontWeight: 700 }}>PENDING</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Scorekeeper Actions — active: close only */}
        {table?.status === 'active' && canScore && (
          <div style={s.actionsCard}>
            <div style={s.sectionTitle}>SCOREKEEPER</div>
            <button style={s.closeTableBtn} onClick={handleCloseTable}>Close Table — Return Funds</button>
          </div>
        )}

        {/* Scorekeeper Actions — settled: next round + close */}
        {table?.status === 'settled' && canScore && (
          <div style={s.actionsCard}>
            <div style={s.sectionTitle}>SCOREKEEPER</div>
            <button style={{ ...s.nextRoundBtn, opacity: acting ? 0.7 : 1 }} onClick={handleNextRound} disabled={acting}>
              ▶ Next Round
            </button>
            <button style={s.closeTableBtn} onClick={handleCloseTable}>Close Table</button>
          </div>
        )}

        {/* Settled scorekeeper actions */}

        {/* Invite */}
        {(table?.status === 'pending' || table?.status === 'active') && isCreator && (
          <>
            <button style={s.inviteToggle} onClick={() => setShowInvite(v => !v)}>
              {showInvite ? '▲ Hide' : '+ Invite Players'}
            </button>
            {showInvite && (
              <div style={s.inviteCard}>
                <input style={s.input} placeholder="Search by name or @username"
                  value={searchQuery} onChange={e => handleSearch(e.target.value)} />
                {searchResults.map(p => (
                  <button key={p.id} style={s.searchResult} onClick={() => handleInvite(p)}>
                    <div style={s.searchName}>{p.first_name} {p.last_name}</div>
                    <div style={s.searchUser}>@{p.username}</div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  toast: { position: 'fixed', top: 0, left: 0, right: 0, padding: '16px 24px', color: 'white', fontSize: 14, fontWeight: 600, textAlign: 'center', zIndex: 100 },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 12 },
  potCard: { background: '#0A0A0A', borderRadius: 20, padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  potLabel: { fontSize: 10, fontWeight: 700, color: '#444', letterSpacing: 1.5 },
  potAmt: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, color: '#C19A6B', lineHeight: 1 },
  potSub: { fontSize: 11, color: '#555', marginTop: 4 },
  roundBadge: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: '#FDF9E7' },
  statusBadge: { fontSize: 10, fontWeight: 700, letterSpacing: 1 },
  winnerBanner: { background: 'linear-gradient(135deg,#C19A6B,#9B7D52)', borderRadius: 16, padding: '16px 20px', color: 'white', fontSize: 18, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, textAlign: 'center' },
  startBtn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' },
  pendingNotice: { background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#9B7D52', textAlign: 'center' },
  sectionTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 15, color: '#7A7570', letterSpacing: 1.5 },
  playersList: { background: '#FFFFFF', borderRadius: 20, border: '1px solid #E0DDD4', overflow: 'hidden' },
  playerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #EDE8D8' },
  playerInfo: { flex: 1 },
  playerName: { fontSize: 14, fontWeight: 700, color: '#1A1814' },
  youTag: { fontSize: 11, color: '#00563F', fontWeight: 700 },
  skTag: { fontSize: 12 },
  rebuyTag: { fontSize: 11, color: '#C19A6B', marginTop: 2 },
  playerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  livesRow: { display: 'flex', gap: 3 },
  lifeHeart: { width: 20, height: 20, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'white', fontWeight: 700 },
  loseLifeBtn: { background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, color: '#C0392B', cursor: 'pointer', fontFamily: 'inherit' },
  loseLifeBtnConfirm: { background: '#C0392B', color: 'white', border: '1px solid #C0392B' },
  emptyRow: { padding: '16px', textAlign: 'center', color: '#7A7570', fontSize: 13 },
  actionsCard: { background: '#FFFFFF', borderRadius: 20, padding: 18, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 10 },
  nextRoundBtn: { background: '#0A0A0A', color: '#FDF9E7', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  closeTableBtn: { background: 'transparent', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 12, padding: '11px', fontSize: 13, fontWeight: 600, color: '#C0392B', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' },
  rebuyBtn: { background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, color: '#9B7D52', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  inviteToggle: { background: 'transparent', border: '1.5px dashed #00563F', borderRadius: 14, padding: '13px', fontSize: 14, fontWeight: 700, color: '#00563F', cursor: 'pointer', fontFamily: 'inherit' },
  inviteCard: { background: '#FFFFFF', borderRadius: 16, padding: 16, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 8 },
  input: { background: '#FDF9E7', border: '1.5px solid #D0CCC0', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  searchResult: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#FDF9E7', borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', width: '100%' },
  searchName: { fontSize: 14, fontWeight: 600, color: '#1A1814' },
  searchUser: { fontSize: 12, color: '#7A7570' },
}
